import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { QRCodeSVG } from 'qrcode.react';

const WalkinDriveCard = ({ drive }) => {
  const [showQR, setShowQR] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const applyUrl = `${window.location.origin}/walkin-apply/${drive?.qr_token || drive?.id}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(applyUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 3000);
  };

  const handleDownloadQR = () => {
    const svg = document.querySelector(`#qr-walkin-${drive?.id} svg`);
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 250;
    canvas.height = 250;
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = `${(drive?.title || 'walkin-drive').replace(/\s+/g, '_')}_qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const formatDateRange = (start, end) => {
    if (!start && !end) return 'Ongoing Event';
    if (start === end || !end) return start;
    return `${start} to ${end}`;
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-card via-card to-primary/5 p-5 md:p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:border-primary/60">
      {/* Top Banner & Status */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 pb-3 border-b border-border/60">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/15 text-primary font-extrabold text-xs uppercase tracking-wider shadow-sm">
            <span className="w-2 h-2 rounded-full bg-primary animate-ping" />
            <Icon name="Zap" size={13} />
            Walk-in Hiring Drive
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold text-xs">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Active Drive
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={handleCopyLink}
            className="px-2.5 py-1 rounded-lg border border-border bg-background hover:bg-muted text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 shadow-sm"
            title="Copy Drive Application Link"
          >
            <Icon name={copiedLink ? "Check" : "Share2"} size={13} />
            <span>{copiedLink ? "Copied!" : "Share"}</span>
          </button>

          <button
            onClick={() => setShowQR(true)}
            className="px-2.5 py-1 rounded-lg border border-border bg-background hover:bg-muted text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 shadow-sm"
            title="View Drive QR Code"
          >
            <Icon name="QrCode" size={14} />
            <span>QR Code</span>
          </button>
        </div>
      </div>

      {/* Main Details */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-xl md:text-2xl font-black text-foreground mb-1 tracking-tight">
            {drive?.title}
          </h3>
          {drive?.company && (
            <p className="text-sm font-bold text-primary mb-2 flex items-center gap-1.5">
              <Icon name="Building2" size={15} />
              <span>Hiring for {drive?.company}</span>
              {drive?.company_industry && (
                <span className="text-xs text-muted-foreground font-normal">({drive?.company_industry})</span>
              )}
            </p>
          )}

          {/* Highlights & Meta */}
          <div className="flex flex-wrap gap-2 my-3 text-xs">
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-card border border-border text-foreground font-semibold shadow-sm">
              <Icon name="Calendar" size={14} className="text-primary" />
              <span>Date: {formatDateRange(drive?.drive_date_start, drive?.drive_date_end)}</span>
            </span>

            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-card border border-border text-foreground font-semibold shadow-sm">
              <Icon name="MapPin" size={14} className="text-primary" />
              <span>Venue: {drive?.venue ? `${drive.venue}, ` : ''}{drive?.location}</span>
            </span>

            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-primary/10 text-primary font-bold shadow-sm">
              <Icon name="Users" size={14} />
              <span>{drive?.panel_count ? `${drive.panel_count} Interview Panels` : 'Multi-Panel Queuing'}</span>
            </span>
          </div>

          {/* Open Roles */}
          {drive?.role_requirements && (
            <p className="text-xs text-muted-foreground bg-muted/30 p-2.5 rounded-xl border border-border/80 line-clamp-2 mt-2 leading-relaxed">
              <strong className="text-foreground">Open Roles / Requirements:</strong> {drive.role_requirements}
            </p>
          )}
        </div>

        {/* Action Button */}
        <div className="flex md:flex-col items-center justify-end gap-2 flex-shrink-0 pt-2 md:pt-0">
          <Link
            to={`/walkin-apply/${drive?.qr_token || drive?.id}`}
            className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-extrabold text-sm shadow-lg shadow-primary/25 hover:bg-primary/90 transition-all hover:scale-105"
          >
            <span>Register / Walk In</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
          <span className="text-[11px] text-muted-foreground font-medium text-center hidden md:block">
            ⚡ Instant panel queue assignment
          </span>
        </div>
      </div>

      {/* QR Modal */}
      {showQR && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={() => setShowQR(false)}
        >
          <div
            className="bg-card border border-border rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-3">
              <Icon name="QrCode" size={24} />
            </div>
            <h3 className="font-extrabold text-foreground text-lg mb-1">{drive?.title}</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Scan with your phone to fill out the walk-in form on-site
            </p>

            <div className="flex justify-center p-4 bg-white rounded-2xl shadow-inner mb-4" id={`qr-walkin-${drive?.id}`}>
              <QRCodeSVG
                value={applyUrl}
                size={210}
                level="H"
                includeMargin={true}
              />
            </div>

            <p className="text-[11px] text-muted-foreground font-mono truncate px-2 mb-4 bg-muted/40 py-1.5 rounded-lg">
              {applyUrl}
            </p>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowQR(false)}>
                Close
              </Button>
              <Button variant="default" size="sm" className="flex-1 font-bold shadow-md" onClick={handleDownloadQR}>
                <Icon name="Download" size={14} className="mr-1" /> Download PNG
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalkinDriveCard;
